import { NodeViewWrapper, ReactNodeViewProps } from "@tiptap/react";
import {
  PencilSimpleIcon,
  TrashSimpleIcon,
  ChartBarIcon,
  ArrowClockwiseIcon,
  WarningIcon,
  SparkleIcon
} from "@phosphor-icons/react";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { useTranslation } from "react-i18next";
import $api from "@/zapi-frontend/shared/api/axios";
import { useChartGenerationStore } from "@/zapi-frontend/widgets/TipTapEditor/model/chartGenerationStore";

type Mode = "view" | "edit" | "edit-loading" | "refresh-loading";

type ChartAttrs = {
  chartId: string;
  title: string;
  imageUrl: string;
  sourceText: string;
  prompt: string;
  status: "default" | "stale";
};

function getProjectIdFromPath(): string | null {
  const match = window.location.pathname.match(/\/projects\/(\d+)/);
  return match ? match[1] : null;
}

export function ChartBlockView({ node, editor, getPos, updateAttributes }: ReactNodeViewProps) {
  const { t } = useTranslation();
  const { chartId, title, imageUrl, status, sourceText, prompt } = node.attrs as ChartAttrs;

  const [isHovered, setIsHovered] = useState(false);
  const [mode, setMode] = useState<Mode>("view");
  const [editPrompt, setEditPrompt] = useState("");
  const [nameDraft, setNameDraft] = useState(title);
  const [editError, setEditError] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  // The node re-hydrates as an empty placeholder on chapter re-entry (the DB markdown
  // is a placeholder — the image was never persisted while the chapter was unmounted),
  // then reconcile stamps the URL a beat later. When this session already holds the
  // result in the generation store, render it directly so no "Creating chart..." loader
  // flashes in that gap. Reconcile/poll still write the attr for persistence.
  const storeResult = useChartGenerationStore((state) => {
    const entry = state.generations[chartId];
    return entry?.status === "done" ? entry.result : undefined;
  });
  const effectiveImageUrl = imageUrl || storeResult?.imageUrl || "";
  const effectiveTitle = title || storeResult?.title || "";

  const canEdit = editor.isEditable;
  const isCreating = !effectiveImageUrl;
  const isStale = status === "stale" && !isCreating;
  const isRefreshLoading = mode === "refresh-loading";
  const isEditLoading = mode === "edit-loading";
  const isEditMode = mode === "edit" || mode === "edit-loading";
  const showLoadingPlaceholder = isCreating || isRefreshLoading || isEditLoading;
  const trimmedName = nameDraft.trim();
  const nameChanged = trimmedName.length > 0 && trimmedName !== title;
  const canSubmitUpdate = (editPrompt.trim().length > 0 || nameChanged) && !isEditLoading;
  const bottomError = isEditMode ? editError : isStale ? refreshError : null;

  const loadingMessage = isCreating
    ? "Creating chart..."
    : isRefreshLoading
    ? "Refreshing chart..."
    : "Updating chart...";

  const pollAttemptsRef = useRef(0);
  useEffect(() => {
    if (effectiveImageUrl) return;
    const projectId = getProjectIdFromPath();
    if (!projectId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const POLL_INTERVAL_MS = 4000;
    const MAX_ATTEMPTS = 30;

    const poll = async () => {
      if (cancelled) return;
      pollAttemptsRef.current += 1;
      try {
        const res = await $api.get<{
          data: {
            generations: { chartId: string; status: "done" | "empty" | "failed"; imageUrl?: string; title?: string }[];
          };
        }>(`/projects/${projectId}/images/generations`, { params: { chartIds: chartId } });
        if (cancelled) return;
        const generation = res.data.data.generations.find((g) => g.chartId === chartId);
        if (generation?.status === "done" && generation.imageUrl) {
          updateAttributes({ imageUrl: generation.imageUrl, title: generation.title || title, status: "default" });
          return;
        }
        if (generation && (generation.status === "empty" || generation.status === "failed")) {
          const pos = getPos();
          if (pos !== undefined)
            editor
              .chain()
              .deleteRange({ from: pos, to: pos + node.nodeSize })
              .run();
          return;
        }
      } catch {
        // Transient error — fall through to retry.
      }
      if (!cancelled && pollAttemptsRef.current < MAX_ATTEMPTS) {
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    timer = setTimeout(poll, 2000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveImageUrl, chartId]);

  const handleDelete = () => {
    const pos = getPos();
    if (pos === undefined) return;
    editor
      .chain()
      .focus()
      .deleteRange({ from: pos, to: pos + node.nodeSize })
      .run();
  };

  const handleEditClick = () => {
    setEditError(null);
    setNameDraft(title);
    setEditPrompt("");
    setMode("edit");
  };

  const handleCancel = () => {
    setEditPrompt("");
    setNameDraft(title);
    setEditError(null);
    setMode("view");
  };

  const regenerate = async (nextPrompt: string) => {
    const projectId = getProjectIdFromPath();
    if (!projectId) throw new Error("Project not found");
    const res = await $api.post<{ data: { visualize: boolean; imageUrl: string; label: string } }>(
      `/projects/${projectId}/images/generate`,
      { selectedText: sourceText || title, prompt: nextPrompt, mode: "update", chartId }
    );
    return res.data.data;
  };

  const handleUpdate = async () => {
    const trimmedPrompt = editPrompt.trim();

    // Name-only change: no prompt typed → just save the new title locally.
    // No backend call, no regeneration, no chapter reload.
    if (!trimmedPrompt) {
      if (nameChanged) updateAttributes({ title: trimmedName });
      setMode("view");
      return;
    }

    setEditError(null);
    setMode("edit-loading");
    try {
      const { visualize, imageUrl: nextUrl, label } = await regenerate(trimmedPrompt);
      if (!visualize) {
        setEditError("Couldn't create a chart from this — try a different instruction.");
        setMode("edit");
        return;
      }
      updateAttributes({
        imageUrl: nextUrl,
        title: nameChanged ? trimmedName : label,
        prompt: trimmedPrompt,
        status: "default"
      });
      setMode("view");
      setEditPrompt("");
    } catch (error) {
      console.error("Failed to update chart:", error);
      setEditError("Failed to update. Please try again.");
      setMode("edit");
    }
  };

  const handlePromptChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setEditPrompt(e.target.value);
    if (editError) setEditError(null);
  };

  const handleRefresh = async () => {
    if (!prompt) {
      setRefreshError("Nothing to refresh from.");
      return;
    }
    setRefreshError(null);
    setMode("refresh-loading");
    try {
      const { visualize, imageUrl: nextUrl, label } = await regenerate(prompt);
      if (!visualize) {
        setRefreshError("Couldn't refresh — nothing to visualize from the source.");
        setMode("view");
        return;
      }
      updateAttributes({ imageUrl: nextUrl, title: label, status: "default" });
      setMode("view");
    } catch (error) {
      console.error("Failed to refresh chart:", error);
      setRefreshError("Failed to refresh. Please try again.");
      setMode("view");
    }
  };

  const showHoverActions = canEdit && isHovered && !isEditMode && !showLoadingPlaceholder;

  return (
    <NodeViewWrapper
      as="div"
      data-chart-block
      contentEditable={false}
      className="my-4 mx-auto w-full max-w-[720px]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div data-drag-handle className="bg-white border border-innoveraZinc-150 rounded-lg overflow-hidden">
        <div className="flex flex-col gap-2 p-3">
          <div className="flex items-center justify-between h-6 gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <ChartBarIcon className="h-4 w-4 shrink-0 text-zinc-900" />
              <FigmaTypography component="span" variant="label-2" className="truncate leading-[22px] text-zinc-900">
                {effectiveTitle || "Untitled chart"}
              </FigmaTypography>
              {isStale && (
                <span className="ml-1 flex items-center gap-1 text-[12px] leading-[18px] font-medium text-amber-600 shrink-0">
                  <WarningIcon className="h-3 w-3" />
                  Source changed
                </span>
              )}
            </div>

            {showHoverActions && (
              <div className="flex items-center gap-2 shrink-0">
                {isStale && (
                  <>
                    <button
                      type="button"
                      data-testid="chart-block-refresh-button"
                      onClick={handleRefresh}
                      className="flex items-center gap-1 h-6 px-2 rounded-md text-[12px] font-medium leading-[11px] text-zinc-900 hover:bg-zinc-100"
                    >
                      <ArrowClockwiseIcon className="h-3 w-3" />
                      Refresh
                    </button>
                    <div className="h-4 w-px bg-zinc-200" />
                  </>
                )}
                <button
                  type="button"
                  data-testid="chart-block-edit-button"
                  onClick={handleEditClick}
                  className="flex items-center gap-1 h-6 px-2 rounded-md text-[12px] font-medium leading-[11px] text-zinc-900 hover:bg-zinc-100"
                >
                  <PencilSimpleIcon className="h-3 w-3" />
                  Edit
                </button>
                <div className="h-4 w-px bg-zinc-200" />
                <button
                  type="button"
                  data-testid="chart-block-delete-button"
                  onClick={handleDelete}
                  className="flex items-center gap-1 h-6 px-2 rounded-md text-[12px] font-medium leading-[11px] text-red-500 hover:bg-red-50"
                >
                  <TrashSimpleIcon className="h-3 w-3" />
                  Delete
                </button>
              </div>
            )}
          </div>

          <div className="flex min-h-[220px] items-center justify-center gap-3 text-base text-zinc-500">
            {showLoadingPlaceholder ? (
              <>
                <Spinner aria-label={t("loading")} className="size-6 text-muted-foreground" />
                {loadingMessage}
              </>
            ) : effectiveImageUrl ? (
              <img
                src={effectiveImageUrl}
                alt={effectiveTitle}
                draggable={false}
                className="block max-h-[440px] w-auto max-w-full rounded-md object-contain"
              />
            ) : (
              <span className="text-zinc-400">Image unavailable</span>
            )}
          </div>
        </div>

        {isEditMode && (
          <>
            <div className="h-px w-full bg-innoveraZinc-150" />
            <div className="bg-zinc-50 flex flex-col gap-3 p-3">
              <div className="flex flex-col gap-1.5">
                <FigmaTypography component="span" variant="label-2" className="text-zinc-900">
                  Name
                </FigmaTypography>
                <input
                  type="text"
                  data-testid="chart-block-name-input"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  placeholder="Chart name"
                  disabled={isEditLoading}
                  className="block w-full px-3 py-2 text-[14px] leading-[22px] bg-white border border-innoveraZinc-150 rounded-lg focus:outline-hidden focus:border-zinc-400 placeholder:text-zinc-400 disabled:opacity-60"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
              </div>
              <div className="flex items-center gap-2 py-1">
                <PencilSimpleIcon className="h-4 w-4 text-zinc-900 shrink-0" />
                <FigmaTypography component="span" variant="label-2" className="text-zinc-900">
                  What would you like to change?
                </FigmaTypography>
                <span className="bg-violet-100 text-[#7c3aed] text-[12px] font-medium leading-none px-1 py-1 rounded">
                  AI
                </span>
              </div>
              <textarea
                data-testid="chart-block-prompt-textarea"
                value={editPrompt}
                onChange={handlePromptChange}
                placeholder="e.g. Change title, switch to bar chart..."
                disabled={isEditLoading}
                className="block w-full h-24 px-3 py-2 text-[14px] leading-[22px] bg-white border border-innoveraZinc-150 rounded-lg resize-none focus:outline-hidden focus:border-zinc-400 placeholder:text-zinc-400 disabled:opacity-60"
              />
              <div className="flex items-center justify-end gap-1">
                <button
                  type="button"
                  data-testid="chart-block-cancel-button"
                  onClick={handleCancel}
                  disabled={isEditLoading}
                  className="h-8 px-3 rounded-lg border border-innoveraZinc-150 bg-white text-[14px] font-medium text-zinc-900 hover:bg-zinc-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="chart-block-update-button"
                  onClick={handleUpdate}
                  disabled={!canSubmitUpdate}
                  className={
                    canSubmitUpdate
                      ? "h-8 px-3 rounded-lg flex items-center gap-1 text-[14px] font-medium bg-zinc-900 text-white hover:bg-zinc-800"
                      : "h-8 px-3 rounded-lg flex items-center gap-1 text-[14px] font-medium bg-zinc-100 text-zinc-400 cursor-not-allowed"
                  }
                >
                  {isEditLoading ? (
                    <Spinner aria-label={t("loading")} className="size-4" />
                  ) : (
                    <SparkleIcon className="h-4 w-4" />
                  )}
                  {isEditLoading ? "Updating" : "Update"}
                </button>
              </div>
            </div>
          </>
        )}

        {bottomError && !showLoadingPlaceholder && (
          <>
            <div className="h-px w-full bg-innoveraZinc-150" />
            <div className="bg-red-50 flex items-start gap-2 px-3 py-2.5">
              <WarningIcon className="h-4 w-4 text-[#D93D3D] mt-[2px] shrink-0" />
              <FigmaTypography component="span" variant="body-2" className="text-[#D93D3D]">
                {bottomError}
              </FigmaTypography>
            </div>
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
}
